/**
 * `<ReportLookupModal>` + `<ReportLookupButton>` — the paste-box
 * lookup (TRIAG-252).
 *
 * An operator triaging a client report in Slack clicks "Look up" in
 * the top nav, pastes the raw report blob (the empire-admin link, a
 * dashboard URL, or just the UUID), and lands on the bundle's status
 * + a deep link to the long-form analysis — without hand-copying the
 * UUID out of the link and navigating manually.
 *
 * # Resolution (Phase 1 — mobile-first)
 *
 * Extraction is delegated to the pure `lib/report-ref.ts` module
 * (full-UUID-only, no short-prefix guessing). The extracted UUID is
 * resolved by exact `analysis_id` match via the existing
 * `GET /api/bundles/:id` endpoint (which also mints fresh presigned
 * artifact URLs, so the PDF link works straight out of the response).
 * The status badge reuses the existing `deriveRoute(row)` helper so
 * the modal's status vocabulary stays in lockstep with the /bundles
 * table + route-summary chips.
 *
 * NOTE (Phase 1 limitation): web/Usersnap reports synthesize a
 * different `analysis_id` and carry the original in
 * `client_report_uuid`, so pasting a web report's UUID 404s here. The
 * follow-up ticket resolves by `client_report_uuid` too. The 404 copy
 * names this so an operator isn't left guessing.
 *
 * # Why no `useToast`
 *
 * All feedback (loading / not-found / no-UUID / result) renders inline
 * in the modal body — an operator reading the result wants it in front
 * of them, not in a transient corner toast.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link } from "react-router";
import { UserLink } from "@/components/UserLink";

import {
  api,
  ApiError,
  handleSearchUrl,
  type BundleLifecycleRow,
  type HandleSearchResponse,
} from "@/lib/api";
import { deriveRoute, type ChipColor } from "@/lib/pipelineStages";
import { cn } from "@/lib/utils";
import { extractReportUuids } from "@/lib/report-ref";
import { featureAreaLabel } from "@/lib/feature-area";

/**
 * Tailwind chip fill per `ChipColor`. A compact local map rather than
 * importing `Bundles.tsx::chipClass` (page-private). Covers every
 * `ChipColor` so a new color added to the union surfaces here as a
 * type error.
 */
const CHIP_CLASSES: Record<ChipColor, string> = {
  blue: "bg-blue-50 text-blue-900 dark:bg-blue-950 dark:text-blue-100",
  emerald:
    "bg-emerald-50 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100",
  amber: "bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-100",
  purple:
    "bg-purple-50 text-purple-900 dark:bg-purple-950 dark:text-purple-100",
  slate: "bg-slate-100 text-slate-900 dark:bg-slate-900 dark:text-slate-100",
  gray: "bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100",
  red: "bg-red-50 text-red-900 dark:bg-red-950 dark:text-red-100",
  orange:
    "bg-orange-50 text-orange-900 dark:bg-orange-950 dark:text-orange-100",
  // TRIAG-270 — patchgen route chip (report done, patches + Slack
  // post pending).
  teal: "bg-teal-50 text-teal-900 dark:bg-teal-950 dark:text-teal-100",
  // Galley #11179 — stranded ("Never left intake") route chip.
  fuchsia:
    "bg-fuchsia-50 text-fuchsia-900 dark:bg-fuchsia-950 dark:text-fuchsia-100",
};

/** Shared link styling for the result card + candidate rows. */
const LINK_CLASS =
  "text-blue-700 underline-offset-2 hover:underline dark:text-blue-400";

/**
 * Extract a bare 32-hex server trace id from pasted text, if present.
 * Bounded by non-hex chars so it never matches a SUBSTRING of a longer
 * hex run (e.g. a 64-hex sha). A dashed UUID is 36 chars with hyphens, so
 * it never collides with this 32-hex shape — the caller only reaches here
 * after `extractReportUuids` found no report UUID. Lowercased to match the
 * server's `server_trace_id` auto-detection.
 */
function extractTraceId(text: string): string | null {
  const m = text.match(/(?:^|[^0-9a-fA-F])([0-9a-fA-F]{32})(?:[^0-9a-fA-F]|$)/);
  return m ? m[1].toLowerCase() : null;
}

/** Status chip (label + glyph + color) for a resolved row, via deriveRoute. */
function StatusBadge({ row }: { row: BundleLifecycleRow }) {
  const { label, glyph, color } = deriveRoute(row);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.7rem] font-semibold",
        CHIP_CLASSES[color],
      )}
    >
      <span aria-hidden="true">{glyph}</span>
      {label}
    </span>
  );
}

/** Discriminated lookup state driving the modal body below the input. */
type LookupState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "no_uuid" }
  | { kind: "not_found"; uuid: string }
  | { kind: "error"; message: string }
  | { kind: "choices"; uuids: string[] }
  | { kind: "found"; row: BundleLifecycleRow }
  // TRIAG-276 Deploy 2: the paste was a 32-hex server trace (a backend
  // engineer handing one off) — reverse-resolved to the bundle(s) that
  // captured it via `/api/handles/search`.
  | {
      kind: "trace_matches";
      trace: string;
      uuids: string[];
      truncated: boolean;
    }
  | { kind: "trace_no_match"; trace: string };

interface ReportLookupModalProps {
  onClose: () => void;
}

export function ReportLookupModal({ onClose }: ReportLookupModalProps) {
  const [text, setText] = useState("");
  const [state, setState] = useState<LookupState>({ kind: "idle" });
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Initial focus on the textarea so an operator can paste-and-go
  // without a mouse hop.
  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // ESC closes the modal — matches the project's other modals.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const runLookup = useCallback(async () => {
    const uuids = extractReportUuids(text);
    if (uuids.length === 0) {
      // No report UUID in the paste. It may be a bare 32-hex SERVER
      // TRACE (a backend engineer handing one off) — reverse-resolve it
      // to the bundle(s) that captured it via the existing handle
      // search (which auto-detects 32-hex → server_trace_id). This
      // closes the backend→triage direction without a separate UI.
      const trace = extractTraceId(text);
      if (trace) {
        setState({ kind: "loading" });
        try {
          const res = await api<HandleSearchResponse>(handleSearchUrl(trace));
          const seen = new Set<string>();
          const matchUuids = res.matches
            .map((m) => m.bundle_uuid)
            .filter((u) => (seen.has(u) ? false : (seen.add(u), true)));
          if (matchUuids.length === 0) {
            setState({ kind: "trace_no_match", trace });
          } else {
            setState({
              kind: "trace_matches",
              trace,
              uuids: matchUuids,
              truncated: res.truncated,
            });
          }
        } catch (e) {
          const message =
            e instanceof Error
              ? e.message
              : "Trace lookup failed — please retry.";
          setState({ kind: "error", message });
        }
        return;
      }
      setState({ kind: "no_uuid" });
      return;
    }
    // 2+ DISTINCT reports referenced — never guess; let the operator
    // pick from a resolved list (the "don't silently choose on
    // ambiguity" principle, applied to the paste side).
    if (uuids.length > 1) {
      setState({ kind: "choices", uuids });
      return;
    }
    const uuid = uuids[0];
    setState({ kind: "loading" });
    try {
      const row = await api<BundleLifecycleRow>(`/api/bundles/${uuid}`);
      setState({ kind: "found", row });
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setState({ kind: "not_found", uuid });
        return;
      }
      const message =
        e instanceof Error ? e.message : "Lookup failed — please retry.";
      setState({ kind: "error", message });
    }
  }, [text]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-[10vh]"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-lookup-title"
        className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-5 shadow-xl dark:border-gray-800 dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between">
          <h2
            id="report-lookup-title"
            className="text-base font-semibold text-gray-900 dark:text-gray-50"
          >
            Look up a report
          </h2>
          <button
            type="button"
            aria-label="Close lookup"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:text-gray-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:hover:text-gray-200"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>

        <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
          Paste a client report — the empire-admin link, a dashboard URL, or
          just the full UUID. We extract the report id and resolve it. You can
          also paste a 32-hex <strong>server trace</strong> to find the
          bundle(s) that captured it.
        </p>

        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            // Cmd/Ctrl+Enter submits without leaving the keyboard.
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void runLookup();
            }
          }}
          rows={4}
          placeholder="Report created: http://empire-admin.signal.is/client-reports/…"
          aria-label="Paste report text or UUID"
          className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
        />

        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void runLookup()}
            disabled={state.kind === "loading"}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-60"
          >
            {state.kind === "loading" ? "Looking up…" : "Look up"}
          </button>
        </div>

        <div className="mt-3">
          <LookupBody state={state} onClose={onClose} />
        </div>
      </div>
    </div>
  );
}

/** Renders the result / error / empty body for the current state. */
function LookupBody({
  state,
  onClose,
}: {
  state: LookupState;
  onClose: () => void;
}) {
  switch (state.kind) {
    case "idle":
    case "loading":
      return null;
    case "no_uuid":
      return (
        <p
          role="status"
          className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
        >
          Couldn't find a full report UUID in that text. Paste the empire-admin
          link or the full 36-character UUID — short prefixes aren't supported
          yet.
        </p>
      );
    case "not_found":
      return (
        <p
          role="status"
          className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
        >
          No bundle found with id <code>{state.uuid}</code>. Double-check the
          UUID and try again.
        </p>
      );
    case "error":
      return (
        <p
          role="alert"
          className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-900 dark:border-red-700 dark:bg-red-950/40 dark:text-red-200"
        >
          {state.message}
        </p>
      );
    case "choices":
      return (
        <div>
          <p
            role="status"
            className="mb-2 text-xs text-gray-600 dark:text-gray-400"
          >
            That text references {state.uuids.length} different reports — pick
            the one you mean:
          </p>
          <ul className="space-y-1">
            {state.uuids.map((u) => (
              <CandidateRow key={u} uuid={u} onClose={onClose} />
            ))}
          </ul>
        </div>
      );
    case "found":
      return <ResultCard row={state.row} onClose={onClose} />;
    case "trace_matches":
      return (
        <div>
          <p
            role="status"
            className="mb-2 text-xs text-gray-600 dark:text-gray-400"
          >
            Server trace <code>{state.trace}</code> was captured by{" "}
            {state.uuids.length} bundle{state.uuids.length === 1 ? "" : "s"}
            {state.truncated ? " (showing the most recent)" : ""}:
          </p>
          <ul className="space-y-1">
            {state.uuids.map((u) => (
              <CandidateRow key={u} uuid={u} onClose={onClose} />
            ))}
          </ul>
        </div>
      );
    case "trace_no_match":
      return (
        <p
          role="status"
          className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
        >
          No bundle has captured server trace <code>{state.trace}</code>. The
          user may not have filed a report for this failure, or the bundle
          predates trace capture.
        </p>
      );
  }
}

/** The resolved-bundle result card: status badge + summary + links. */
function ResultCard({
  row,
  onClose,
}: {
  row: BundleLifecycleRow;
  onClose: () => void;
}) {
  return (
    <div className="rounded border border-gray-200 bg-gray-50 p-3 text-xs dark:border-gray-800 dark:bg-gray-950/40">
      <div className="mb-1 flex items-center gap-2">
        <StatusBadge row={row} />
        <code className="truncate text-gray-500 dark:text-gray-400">
          {row.analysis_id}
        </code>
      </div>

      <p className="mb-2 text-sm text-gray-900 dark:text-gray-100">
        {row.one_line ?? "(no summary computed yet)"}
      </p>

      <dl className="mb-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-gray-600 dark:text-gray-400">
        <Meta label="Type" value={row.llm_report_type} />
        <Meta label="Area" value={row.feature_area && featureAreaLabel(row.feature_area)} />
        <Meta
          label="Platform"
          value={
            row.platform
              ? `${row.platform} ${row.app_version ?? ""}`.trim()
              : null
          }
        />
        <Meta
          label="Reporter"
          value={row.username ? <UserLink username={row.username} /> : null}
        />
      </dl>

      <div className="flex flex-wrap gap-x-3 gap-y-1">
        <Link
          to={`/analyses/${row.analysis_id}`}
          onClick={onClose}
          className={cn(LINK_CLASS, "font-medium")}
        >
          Open full analysis →
        </Link>
        <Link
          to={`/bundles/${row.analysis_id}`}
          onClick={onClose}
          className={LINK_CLASS}
        >
          Bundle timeline
        </Link>
        {row.was_deduplicated && row.canonical_analysis_id && (
          <Link
            to={`/duplicates/${row.canonical_analysis_id}`}
            onClick={onClose}
            className={LINK_CLASS}
          >
            Duplicate family
          </Link>
        )}
        {row.pdf_url && (
          <a
            href={row.pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            className={LINK_CLASS}
          >
            PDF report ↗
          </a>
        )}
      </div>
    </div>
  );
}

/**
 * One row in the multi-match pick-list. Resolves its OWN uuid via the
 * existing bundle endpoint so a per-row 404 (e.g. a web/Usersnap report
 * whose `client_report_uuid` differs from `analysis_id`) degrades to a
 * "not found" row without breaking its siblings.
 */
function CandidateRow({
  uuid,
  onClose,
}: {
  uuid: string;
  onClose: () => void;
}) {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "not_found" }
    | { kind: "error" }
    | { kind: "found"; row: BundleLifecycleRow }
  >({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const row = await api<BundleLifecycleRow>(`/api/bundles/${uuid}`);
        if (!cancelled) setState({ kind: "found", row });
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 404) {
          setState({ kind: "not_found" });
        } else {
          setState({ kind: "error" });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uuid]);

  const shortId = `${uuid.slice(0, 8)}…`;
  const mutedRow =
    "rounded border border-gray-200 p-2 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400";

  if (state.kind === "loading") {
    return (
      <li className={mutedRow}>
        Resolving <code>{shortId}</code>
      </li>
    );
  }
  if (state.kind === "not_found") {
    return (
      <li className={mutedRow}>
        <code>{shortId}</code> — no bundle found
      </li>
    );
  }
  if (state.kind === "error") {
    return (
      <li className={mutedRow}>
        <code>{shortId}</code> — lookup failed
      </li>
    );
  }

  const row = state.row;
  return (
    <li className="rounded border border-gray-200 bg-gray-50 p-2 text-xs dark:border-gray-800 dark:bg-gray-950/40">
      <div className="mb-1 flex items-center gap-2">
        <StatusBadge row={row} />
        {row.username && (
          <span className="text-gray-500 dark:text-gray-400">
            <UserLink username={row.username} className="font-normal" />
          </span>
        )}
      </div>
      <p className="mb-1 text-gray-900 dark:text-gray-100">
        {row.one_line ?? "(no summary computed yet)"}
      </p>
      <Link
        to={`/analyses/${row.analysis_id}`}
        onClick={onClose}
        className={cn(LINK_CLASS, "font-medium")}
      >
        Open full analysis →
      </Link>
    </li>
  );
}

/** One label/value row in the result card metadata grid; hidden when empty. */
function Meta({ label, value }: { label: string; value: ReactNode }) {
  if (!value) return null;
  return (
    <div className="flex gap-1">
      <dt className="font-medium">{label}:</dt>
      <dd className="truncate">{value}</dd>
    </div>
  );
}

/**
 * Top-nav entry point: the "Look up" button + the modal it opens.
 * Mounted once in `<TopNav>` so the affordance is global (appears on
 * every authenticated page, including Home and /trends where the
 * originating Slack request started).
 */
export function ReportLookupButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
      >
        <span aria-hidden="true">🔎</span>
        <span>Look up</span>
      </button>
      {open && <ReportLookupModal onClose={() => setOpen(false)} />}
    </>
  );
}
